import {
    AbfLoanExpanded,
    BsMetaUtil,
    calculateLoanLiquidationPrice,
    formatLeverage,
    getZcLedger,
    getZcLoanCollateral,
    isLoanActive,
    LoopPositionExpanded,
    calculateLeverageMultiplier,
    useLoopRecentStartTime,
    isWhirlpoolLoopDeposit,
    LoanRepayType
} from "@bridgesplit/abf-react";
import { MediaQuery, StatProps, Text } from "@bridgesplit/ui";
import { formatDate, formatUsd } from "@bridgesplit/utils";
import { useMediaQuery } from "@mui/material";
import { COPY } from "app/constants/copy";

import {
    useLoopProfitLoss,
    useLoopUnwindAlerts,
    LiquidationPrice,
    LoopProfitLoss,
    LoopInitialDeposit,
    PositionCardStats,
    LoopDisplayType,
    getLoopOracleProfitLoss,
    LoanHealthText
} from "../../common";
import { LoopPositionApy } from "./common";

export default function LoopPositionStats({
    loopPosition,
    query,
    isExpanded,
    displayType,
    setDisplayType,
    unwindType
}: {
    loopPosition: LoopPositionExpanded | undefined;
    query: MediaQuery;
    isExpanded: boolean;
    displayType: LoopDisplayType;
    unwindType: LoanRepayType | undefined;
    setDisplayType: (displayType: LoopDisplayType) => void;
}) {
    const userLoopInfo = loopPosition?.userLoopInfo;
    const loanExpanded = loopPosition?.loanExpanded;

    const gainSummary = useLoopProfitLoss(loopPosition, { skip: !isExpanded }, unwindType);
    const isActive = isLoanActive(loanExpanded);
    const isMobile = useMediaQuery(query.below);
    const { unwindWarningMessage } = useLoopUnwindAlerts({
        loopPositionExpanded: loopPosition,
        summary: gainSummary,
        variant: "preview"
    });

    const firstLedger = getZcLedger(loanExpanded);

    const loopOracleProfitLoss = getLoopOracleProfitLoss(loopPosition);

    const leverageMultiplier = calculateLeverageMultiplier(loopPosition);

    const whirlpoolDeposit = isWhirlpoolLoopDeposit(loopPosition?.userLoopInfo, loopPosition?.loopExpanded);
    const { recentStartTime, isLoading: isLoadingRecentStartTime } = useLoopRecentStartTime(loopPosition);

    const stats: StatProps[] = [
        {
            value: userLoopInfo ? formatLeverage(leverageMultiplier) : undefined,
            caption: COPY.LEVERAGE_TERM,
            hide: !isMobile
        },
        {
            value: <LoopPositionApy loopPosition={loopPosition} />,
            caption: "Net APY",
            hide: !isMobile || !userLoopInfo?.netApyLive
        },
        {
            value: (
                <LoopInitialDeposit
                    loopPosition={loopPosition}
                    displayType={displayType}
                    setDisplayType={setDisplayType}
                />
            ),
            caption: "Original deposit",
            hide: whirlpoolDeposit && (recentStartTime || isLoadingRecentStartTime)
        },
        {
            value: (
                <LoopProfitLoss
                    displayType={displayType}
                    loopPosition={loopPosition}
                    gainSummary={loopOracleProfitLoss}
                />
            ),
            caption: "Est. P&L",
            tooltip: unwindWarningMessage
        },
        {
            value: (
                <Text translate="no">{formatUsd(loopOracleProfitLoss?.contributions.totalCollateralTopUpsUsd)}</Text>
            ),
            caption: `${BsMetaUtil.getSymbol(loopPosition?.loopExpanded?.collateralToken)} top-ups`,
            hide: !loopOracleProfitLoss?.contributions.totalCollateralTopUpsUsd
        },
        {
            value: <Text translate="no">{formatUsd(loopOracleProfitLoss?.contributions.totalPaidUsd)}</Text>,
            caption: `${BsMetaUtil.getSymbol(loopPosition?.loopExpanded?.principalToken)} repayments`,
            hide: !loopOracleProfitLoss?.contributions.totalPaidUsd || !isActive
        },
        {
            caption: "Health",
            value: <LoanHealthText loan={loopPosition?.loanExpanded} />,
            hide: !isMobile || !isActive
        },
        {
            value: <LoopLiqPrice loanExpanded={loanExpanded} />,
            caption: isMobile ? COPY.LIQUIDATION_PRICE_TERM_MOBILE : COPY.LIQUIDATION_PRICE_TERM,
            tooltip: COPY.LIQUIDATION_PRICE_TOOLTIP,
            hide: !isActive
        },
        {
            value: <Text translate="no">{formatUsd(loopPosition?.userLoopInfo.netPositionValue)}</Text>,
            caption: "Withdrawn",
            hide: true
        },
        {
            value: formatDate(firstLedger?.startTime),
            caption: "Start date",
            hide: !firstLedger?.startTime
        }
    ];

    return <PositionCardStats loading={!loopPosition || !gainSummary} stats={stats} />;
}

function LoopLiqPrice({ loanExpanded }: { loanExpanded: AbfLoanExpanded | undefined }) {
    const liquidationPrice = calculateLoanLiquidationPrice(loanExpanded);
    const collateral = getZcLoanCollateral(loanExpanded);
    return (
        <LiquidationPrice
            liquidationPrice={liquidationPrice?.price ?? 0}
            percentChange={liquidationPrice?.percentChange}
            collateral={collateral?.metadata}
            principal={loanExpanded?.principalMetadata}
        />
    );
}
